import { z } from "zod";
import {
  pqrAttachmentMimeTypes,
  pqrCategories,
  pqrMaxAttachmentBytes,
  pqrMaxAttachments,
  pqrTicketRefPattern,
  type PqrFunctionErrorCode,
} from "@/features/pqr/pqr.types";

export const pqrTicketRefSchema = z.string().regex(pqrTicketRefPattern, "pqr.validation.ticketRef");

export const pqrSubmissionSchema = z.object({
  category: z.enum(pqrCategories, { message: "pqr.validation.category" }),
  email: z.string().trim().min(1, "pqr.validation.required").email("pqr.validation.email"),
  message: z
    .string()
    .trim()
    .min(20, "pqr.validation.messageMin")
    .max(2000, "pqr.validation.messageMax"),
  name: z.string().trim().min(1, "pqr.validation.required").max(120, "pqr.validation.nameMax"),
  subject: z.string().trim().min(1, "pqr.validation.required").max(120, "pqr.validation.subjectMax"),
});

export const pqrReplySchema = z.object({
  message: z.string().trim().min(1, "pqr.validation.required").max(2000, "pqr.validation.replyMessageMax"),
});

export type PqrFormValues = z.input<typeof pqrSubmissionSchema>;
export type PqrSubmissionValues = z.output<typeof pqrSubmissionSchema>;
export type PqrReplyFormValues = z.input<typeof pqrReplySchema>;
export type PqrReplyValues = z.output<typeof pqrReplySchema>;

export interface PqrAttachmentValidationError {
  code: PqrFunctionErrorCode;
  fileName?: string;
}

export function isSupportedPqrAttachmentMimeType(value: string) {
  return pqrAttachmentMimeTypes.includes(value as (typeof pqrAttachmentMimeTypes)[number]);
}

export function validatePqrAttachmentFiles(files: File[]): PqrAttachmentValidationError | null {
  if (files.length > pqrMaxAttachments) {
    return {
      code: "PQR_TOO_MANY_ATTACHMENTS",
    };
  }

  for (const file of files) {
    if (!isSupportedPqrAttachmentMimeType(file.type)) {
      return {
        code: "PQR_INVALID_ATTACHMENT_TYPE",
        fileName: file.name,
      };
    }

    if (file.size > pqrMaxAttachmentBytes) {
      return {
        code: "PQR_ATTACHMENT_TOO_LARGE",
        fileName: file.name,
      };
    }
  }

  return null;
}

export function getPqrErrorTranslationKey(code: PqrFunctionErrorCode | "generic") {
  switch (code) {
    case "PQR_UNAUTHORIZED":
      return "pqr.errors.unauthorized";
    case "PQR_INVALID_TICKET_REF":
      return "pqr.errors.invalidTicketRef";
    case "PQR_TOO_MANY_ATTACHMENTS":
      return "pqr.errors.tooManyAttachments";
    case "PQR_INVALID_ATTACHMENT_TYPE":
      return "pqr.errors.invalidAttachmentType";
    case "PQR_ATTACHMENT_TOO_LARGE":
      return "pqr.errors.attachmentTooLarge";
    case "PQR_INVALID_CATEGORY":
      return "pqr.errors.invalidCategory";
    case "PQR_INVALID_NAME":
      return "pqr.errors.invalidName";
    case "PQR_INVALID_EMAIL":
      return "pqr.errors.invalidEmail";
    case "PQR_INVALID_SUBJECT":
      return "pqr.errors.invalidSubject";
    case "PQR_INVALID_MESSAGE":
      return "pqr.errors.invalidMessage";
    case "PQR_INVALID_REPLY_MESSAGE":
      return "pqr.errors.invalidReplyMessage";
    case "PQR_REPLY_UNAVAILABLE":
      return "pqr.errors.replyUnavailable";
    case "PQR_STORAGE_UPLOAD_FAILED":
      return "pqr.errors.uploadFailed";
    case "PQR_INSERT_FAILED":
      return "pqr.errors.insertFailed";
    default:
      return "pqr.errors.generic";
  }
}
